import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {QlService} from 'features/core/shared/ql/service/ql.service';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {ProvisionStatementStatus} from '../../enum/provision-statement-status.enum';
import {CpRunStatementRef} from '../../interface/cp-run-statement-ref.interface';

/**
 * QL lookup of the final agent statements of a run — ONE query per run (never per row), feeding the
 * button states of "Summe je Berater" (download stored statement vs. create).
 */
@Injectable({providedIn: 'root'})
export class CpRunStatementLookupService {

    constructor(private qlService: QlService) {
    }

    /**
     * Loads the ACTIVE statements of a run.
     *
     * @returns map provisionAgentId -> statement; an agent without a final statement is absent. Per
     * agent there is at most one ACTIVE statement; should the server ever deliver two, the higher
     * version wins. Errors propagate so the caller can tell "none" from "unknown".
     */
    getActiveStatementsByAgent(provisionRunId: number): Observable<Map<number, CpRunStatementRef>> {

        const queryName = 'cpRunActiveAgentStatements';

        return this.qlService.builder()
            .showErrors(false)
            .query({
                name: queryName,
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'cp.ProvisionAgentStatement',
                    as: 'e',
                    fields: [
                        {name: 'id'},
                        {name: 'provisionAgentId'},
                        {name: 'statementNumber'},
                        {name: 'version'},
                        {name: 'dmDocumentId'}
                    ]
                },
                filters: [
                    FilterCriteria.create('e.provisionRunId', FilterCriteria.cOperatorEqual, provisionRunId),
                    FilterCriteria.create('e.status', FilterCriteria.cOperatorEqual, ProvisionStatementStatus.ACTIVE)
                ],
                sortings: [new Sorting('e.version', true)]
            })
            .execute()
            .pipe(map(result => {
                const pipe = result.getPipeResult(queryName);
                if (!pipe || (pipe.status !== undefined && pipe.status !== null && pipe.status !== 0)) {
                    throw new Error('QL query ' + queryName + ' failed');
                }
                const byAgent = new Map<number, CpRunStatementRef>();
                for (const row of (result.get(queryName) ?? [])) {
                    const ref: CpRunStatementRef = {
                        statementId: Number(row['e.id']),
                        provisionAgentId: Number(row['e.provisionAgentId']),
                        statementNumber: row['e.statementNumber'],
                        version: Number(row['e.version']),
                        dmDocumentId: row['e.dmDocumentId'] ?? null
                    };
                    const existing = byAgent.get(ref.provisionAgentId);
                    if (!existing || ref.version > existing.version) {
                        byAgent.set(ref.provisionAgentId, ref);
                    }
                }
                return byAgent;
            }));
    }
}
